import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Anchor, Loader2, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import analytics from "@/lib/segment";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { isCaptchaEnabled } from "@/lib/captcha";
import { EmailConfirmationBanner } from "@/components/auth/EmailConfirmationBanner";
import { Helmet } from "react-helmet-async";

type PlanTier = "free" | "pro" | "scale" | "fleet";
type BillingInterval = "monthly" | "annual";

const planLabels: Record<PlanTier, string> = {
  free: "Free",
  pro: "Pro",
  scale: "Scale",
  fleet: "Fleet",
};

export default function Auth() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const selectedPlan = (searchParams.get("plan") as PlanTier) || "free";
  const billingInterval = (searchParams.get("billing") as BillingInterval) || "monthly";
  const sourceDiscountParam = searchParams.get("source") || "";
  const emailParam = searchParams.get("email");
  const modeParam = searchParams.get("mode");
  const [isLogin, setIsLogin] = useState(modeParam !== "signup");
  const [email, setEmail] = useState(emailParam || "");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const signupCompleteRef = useRef(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string>();
  const captchaRef = useRef<TurnstileInstance>(null);
  const [inviteDetails, setInviteDetails] = useState<{
    companyName: string;
    companyId: string;
    email: string;
  } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for invite token on mount via edge function (secure server-side validation)
  useEffect(() => {
    const checkInvite = async () => {
      if (!inviteToken) return;
      
      try {
        const { data, error } = await supabase.functions.invoke("validate-invitation", {
          body: { token: inviteToken },
        });

        if (error || !data?.valid) {
          toast({
            title: "Invalid or expired invitation",
            description: "Please request a new invitation from your team owner.",
            variant: "destructive",
          });
          return;
        }

        setInviteDetails({
          companyName: data.companyName || "Unknown Company",
          companyId: data.companyId,
          email: data.email,
        });
        setEmail(data.email);
        setIsLogin(false); // Force signup mode for invites
      } catch (err) {
        console.error("Error validating invitation:", err);
        toast({
          title: "Error validating invitation",
          description: "Please try again or request a new invitation.",
          variant: "destructive",
        });
      }
    };
    checkInvite();
  }, [inviteToken, toast]);

  useEffect(() => {
    // Check if user is already logged in using onAuthStateChange
    // This fires immediately with current session state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session && (event === "INITIAL_SESSION" || event === "SIGNED_IN")) {
          if (signupCompleteRef.current) return;
          (async () => {
            try {
              const { data: membership } = await supabase
                .from("company_members")
                .select("company_id, companies(intended_subscription_tier, intended_source_discount)")
                .eq("user_id", session.user.id)
                .maybeSingle();

              const company = membership?.companies as { intended_subscription_tier?: string; intended_source_discount?: string } | null;
              const intendedTier = company?.intended_subscription_tier;
              const intendedSourceDiscount = company?.intended_source_discount;

              if (intendedTier && intendedTier !== "free") {
                const sourceDiscountQs = intendedSourceDiscount ? `&source=${encodeURIComponent(intendedSourceDiscount)}` : "";
                navigate(`/checkout-redirect?plan=${intendedTier}&billing=monthly${sourceDiscountQs}`, { replace: true });
                return;
              }
            } catch (err) {
              console.error("Failed to check intended tier:", err);
            }
            navigate("/app/dashboard", { replace: true });
          })();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken(undefined);
  };


  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (import.meta.env.DEV) {
      console.log("[Auth] handlePasswordSubmit called, isLogin:", isLogin);
    }
    setLoading(true);

    try {
      if (isLogin) {
        if (import.meta.env.DEV) {
          console.log("[Auth] Calling signInWithPassword...");
        }
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: { captchaToken },
        });
        resetCaptcha();
        if (import.meta.env.DEV) {
          console.log("[Auth] signInWithPassword result:", { data: !!data, error });
        }
        if (error) throw error;
        
        // Track successful sign in
        analytics.track("Signed In", {
          method: "password",
        });

        // Clear loading before navigation to prevent unmount issues
        setLoading(false);
        if (import.meta.env.DEV) {
          console.log("[Auth] Login successful, navigating to launch-kit");
        }
        toast({ title: "Welcome back!" });

        // Check if user has a pending plan intent
        try {
          const { data: membership } = await supabase
            .from("company_members")
            .select("company_id, companies(intended_subscription_tier, intended_source_discount)")
            .eq("user_id", data.user.id)
            .maybeSingle();

          const company = membership?.companies as { intended_subscription_tier?: string; intended_source_discount?: string } | null;
          const intendedTier = company?.intended_subscription_tier;
          const intendedSourceDiscount = company?.intended_source_discount;

          if (intendedTier && intendedTier !== "free") {
            const sourceDiscountQs = intendedSourceDiscount ? `&source=${encodeURIComponent(intendedSourceDiscount)}` : "";
            navigate(`/checkout-redirect?plan=${intendedTier}&billing=monthly${sourceDiscountQs}`, { replace: true });
            return;
          }
        } catch (err) {
          console.error("Failed to check intended tier:", err);
        }
        navigate("/app/dashboard", { replace: true });
        return; // Exit early - don't hit finally
      } else {
        // Signup flow - company is created by database trigger
        const emailPrefix = email.split("@")[0];
        const nameForFallback = fullName.trim() || emailPrefix;
        const signupCompanyName = inviteToken ? undefined : (companyName.trim() || `${nameForFallback}'s Company`);
        
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
            captchaToken,
            data: {
              full_name: fullName,
              subscription_tier: selectedPlan,
              company_name: signupCompanyName,
              ...(sourceDiscountParam ? { source_discount: sourceDiscountParam } : {}),
            },
          },
        });
        resetCaptcha();
        if (signUpError) throw signUpError;
        
        if (!authData.user) {
          throw new Error("Failed to create user");
        }

        // Check if user already exists (Supabase returns user with empty identities array)
        // This prevents email enumeration while still providing good UX
        if (authData.user.identities && authData.user.identities.length === 0) {
          toast({
            title: "Account already exists",
            description: "Please sign in with your existing account.",
          });
          // Switch to login mode - clear password, update state, then stop loading
          setPassword("");
          setIsLogin(true);
          setLoading(false);
          return;
        }

        if (inviteToken && inviteDetails) {
          // Joining via invite - add as member (company already exists, trigger skips company creation)
          const { error: memberError } = await supabase
            .from("company_members")
            .insert({
              company_id: inviteDetails.companyId,
              user_id: authData.user.id,
              role: "member",
            });
          
          if (memberError) throw memberError;

          // Mark invitation as accepted
          await supabase
            .from("company_invitations")
            .update({ accepted_at: new Date().toISOString() })
            .eq("token", inviteToken);

          // Track signup with invite
          analytics.track("Signed Up", {
            plan_type: "free",
            source: "invite",
            company_name: inviteDetails.companyName,
          });

          setLoading(false);
          toast({ 
            title: "Welcome to " + inviteDetails.companyName + "!", 
            description: "You've joined the team." 
          });
          navigate("/app/dashboard", { replace: true });
          return;
        } else {
          // New signup - company created by trigger using company_name from metadata
          // Track organic signup
          analytics.track("Signed Up", {
            plan_type: selectedPlan,
            source: "organic",
            company_name: signupCompanyName,
          });

          // Add to ActiveCampaign (fire-and-forget)
          supabase.functions.invoke("subscribe-activecampaign", {
            body: { email },
          }).catch((err) => console.error("Failed to add to ActiveCampaign:", err));

          // Notify admin of new signup (fire-and-forget)
          supabase.functions.invoke("notify-admin", {
            body: {
              type: "signup",
              email,
              fullName: fullName || undefined,
              companyName: signupCompanyName || undefined,
              source: sourceDiscountParam || undefined,
            },
          }).catch((err) => console.error("Failed to send signup notification:", err));

          if (!authData.session) {
            setLoading(false);
            setSignupEmail(email);
            signupCompleteRef.current = true;
            setSignupComplete(true);
            toast({ 
              title: "Account created!", 
              description: "Please check your email to confirm your account." 
            });
            return;
          }
          // If authData.session is present, email confirmation is disabled/auto-confirmed.
          // The onAuthStateChange listener will navigate automatically.
        }
      }
    } catch (error: any) {
      // Track authentication error
      analytics.track("Error Occurred", {
        error_type: "authentication",
        error_message: error.message,
        action: isLogin ? "sign_in" : "sign_up",
      });

      resetCaptcha();
      setLoading(false);

      // Check for email not confirmed error and show friendly message
      const isEmailNotConfirmed = error.message?.toLowerCase().includes("email not confirmed");
      if (isEmailNotConfirmed) {
        setShowEmailConfirmation(true);
        toast({
          title: "Email verification required",
          description: "Please check your email to confirm your account.",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const isJoiningViaInvite = !!inviteToken && !!inviteDetails;

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
      <Helmet>
        <link rel="canonical" href="https://hostcaptain.co/auth" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white">
            <Anchor className="h-8 w-8 text-accent" />
            <span className="text-2xl font-bold">HostCaptain</span>
          </Link>
        </div>

        <Card className="bg-card">
          {signupComplete ? (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-foreground">
                  Verify your email
                </CardTitle>
                <CardDescription>
                  One last step before you can access your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <EmailConfirmationBanner email={signupEmail} />
                <p className="text-sm text-muted-foreground text-center">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => {
                      setSignupComplete(false);
                      setIsLogin(true);
                    }}
                  >
                    try signing in
                  </button>
                  .
                </p>
              </CardContent>
            </>
          ) : (
          <>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-foreground">
              {isJoiningViaInvite 
                ? `Join ${inviteDetails.companyName}`
                : isLogin 
                  ? "Welcome back" 
                  : "Create your account"}
            </CardTitle>
            <CardDescription>
              {isJoiningViaInvite
                ? "Complete your account to join the team"
                : isLogin 
                  ? "Sign in to access your dashboard" 
                  : "Start managing your Turo business like a pro"}
            </CardDescription>
            {!isLogin && !isJoiningViaInvite && (
              <div className="mt-3 inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium">
                Signing up for the {planLabels[selectedPlan]} plan
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Email confirmation banner */}
            {showEmailConfirmation && isLogin && (
              <Alert className="mb-4 border-primary/20 bg-primary/5">
                <Mail className="h-4 w-4 text-primary" />
                <AlertDescription className="text-foreground">
                  Please check your email to confirm your account before signing in.
                </AlertDescription>
              </Alert>
            )}

            {/* Password Form */}
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Smith"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required={!isLogin}
                  />
                </div>
              )}
              {!isLogin && !isJoiningViaInvite && (
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="My Rental Business"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required={!isLogin && !isJoiningViaInvite}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isJoiningViaInvite}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              {isCaptchaEnabled() && (
                <div className="flex justify-center">
                  <Turnstile
                    ref={captchaRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(undefined)}
                  />
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-gradient-primary hover:opacity-90"
                disabled={loading || (isCaptchaEnabled() && !captchaToken)}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isJoiningViaInvite 
                  ? "Join Team"
                  : isLogin 
                    ? "Sign In" 
                    : "Create Account"}
              </Button>
            </form>

            {!isJoiningViaInvite && (
              <div className="mt-6 space-y-2 text-center text-sm">
                {isLogin && (
                  <div>
                    <Link
                      to="/forgot-password"
                      className="text-primary hover:underline font-medium"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                  </span>
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => setIsLogin(!isLogin)}
                  >
                    {isLogin ? "Sign up" : "Sign in"}
                  </button>
                </div>
              </div>
            )}
          </CardContent>
          </>
          )}
        </Card>

        <p className="text-center text-sm text-white/60 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
